/**
 * Cover stat card classification per Amendment §A4 + §S1.
 *
 * Threshold-only — severity NEVER influences cover counts (eradicates D3
 * cover/detail coherence break).
 *
 *   Critical      = status_threshold = EXCEEDED AND
 *                   category_number ∈ HIGH_IMPACT_CATEGORIES (Logic, CP, Float, Data).
 *   Warnings      = status_threshold = EXCEEDED AND
 *                   category_number ∉ HIGH_IMPACT_CATEGORIES.
 *   Passed        = status_threshold = PASS.
 *   Informational = status_threshold = REPORT_ONLY (PDF-POLISH-1 D9).
 *                   These checks emit context (counts, distributions) without
 *                   judging the schedule against a threshold.
 *
 * Excluded entirely: hidden checks (hidden:true OR status_threshold=HIDDEN).
 *
 * Sanity invariant: critical + warnings + passed + informational ===
 *                   total visible checks. (Locked by test.)
 *
 * G2.3.5: §S2 verdict narrative now lives in src/engine/verdict.js
 * (`buildVerdictNarrative` — single source of truth across PDF cover and
 * dashboard). The old `buildCoverNarrative` was deleted.
 *
 * @module coverClassification
 */

import { HIGH_IMPACT_CATEGORIES, STATUS_THRESHOLD } from '../checks/checkResult.js';

function isVisible(check) {
  if (!check) return false;
  if (check.hidden) return false;
  if (check.status_threshold === STATUS_THRESHOLD.HIDDEN) return false;
  return true;
}

/**
 * Classify all canonical CheckResults into Critical / Warnings / Passed /
 * Informational buckets.
 *
 * @param {Array<object>} allChecks - Flat array of canonical CheckResults
 * @returns {{
 *   critical:      { count: number, checks: Array<object> },
 *   warnings:      { count: number, checks: Array<object> },
 *   passed:        { count: number, checks: Array<object> },
 *   informational: { count: number, checks: Array<object> },
 * }}
 */
export function classifyForCover(allChecks) {
  const critical = [];
  const warnings = [];
  const passed = [];
  const informational = [];

  for (const check of allChecks || []) {
    if (!isVisible(check)) continue;

    // G-3 formalised (v3.12): STRUCTURAL out-of-band findings are
    // EXCEEDED-equivalent for S1 cover counting BY CONSTRUCTION — binary
    // checks (#20) and range checks (#21, #29) emit status_threshold
    // EXCEEDED when broken/out-of-band (deriveStatusThreshold), so they flow
    // through this branch identically to ratio checks. This is asserted by
    // test (cover counts include structural EXCEEDED), not left as
    // convention.
    if (check.status_threshold === STATUS_THRESHOLD.EXCEEDED) {
      if (HIGH_IMPACT_CATEGORIES.has(check.category_number)) {
        critical.push(check);
      } else {
        warnings.push(check);
      }
    } else if (check.status_threshold === STATUS_THRESHOLD.PASS) {
      passed.push(check);
    } else if (check.status_threshold === STATUS_THRESHOLD.REPORT_ONLY) {
      informational.push(check);
    }
  }

  return {
    critical:      { count: critical.length,      checks: critical },
    warnings:      { count: warnings.length,      checks: warnings },
    passed:        { count: passed.length,        checks: passed },
    informational: { count: informational.length, checks: informational },
  };
}
